import Vue from 'vue';
import { normalizeTimeEntryId } from '@/service/payroll/time-entry-locks';

const state = {
  entryLocks: {},
};

const getters = {
  getPayrollEntryLockState: (storeState) => (rawTimeEntryId) => {
    const timeEntryId = normalizeTimeEntryId(rawTimeEntryId);
    if (timeEntryId === null) {
      return null;
    }

    return storeState.entryLocks[timeEntryId] || null;
  },

  isPayrollEntryLocked: (storeState, storeGetters) => (rawTimeEntryId) => {
    const lockState = storeGetters.getPayrollEntryLockState(rawTimeEntryId);
    if (!lockState) {
      return false;
    }

    return Object.keys(lockState).some((operation) => Boolean(lockState[operation]));
  },

  isPayrollEntryLockedBy: (storeState, storeGetters) => (rawTimeEntryId, operation) => {
    if (!operation) {
      return false;
    }

    const lockState = storeGetters.getPayrollEntryLockState(rawTimeEntryId);
    if (!lockState) {
      return false;
    }

    return Boolean(lockState[operation]);
  },

  hasAnyPayrollEntryLock: (storeState) => {
    return Object.keys(storeState.entryLocks).some((timeEntryId) => {
      const lockState = storeState.entryLocks[timeEntryId];
      if (!lockState) {
        return false;
      }

      return Object.keys(lockState).some((operation) => Boolean(lockState[operation]));
    });
  },
};

const mutations = {
  SET_PAYROLL_ENTRY_LOCK(storeState, payload = {}) {
    const timeEntryId = normalizeTimeEntryId(payload.timeEntryId);
    const operation = payload.operation;

    if (timeEntryId === null || !operation) {
      return;
    }

    if (!storeState.entryLocks[timeEntryId]) {
      Vue.set(storeState.entryLocks, timeEntryId, {});
    }

    Vue.set(storeState.entryLocks[timeEntryId], operation, true);
  },

  CLEAR_PAYROLL_ENTRY_LOCK(storeState, payload = {}) {
    const timeEntryId = normalizeTimeEntryId(payload.timeEntryId);
    const operation = payload.operation;

    if (timeEntryId === null) {
      return;
    }

    const lockState = storeState.entryLocks[timeEntryId];
    if (!lockState) {
      return;
    }

    if (operation) {
      Vue.set(lockState, operation, false);
    }

    const hasAnyLock = Object.keys(lockState).some((name) => Boolean(lockState[name]));
    if (!hasAnyLock) {
      Vue.delete(storeState.entryLocks, timeEntryId);
    }
  },

  CLEAR_ALL_PAYROLL_ENTRY_LOCKS(storeState) {
    storeState.entryLocks = {};
  },
};

const actions = {
  acquirePayrollEntryLock({ state: storeState, getters: storeGetters, commit }, payload = {}) {
    const timeEntryId = normalizeTimeEntryId(payload.timeEntryId);
    const operation = payload.operation;

    if (timeEntryId === null || !operation) {
      return false;
    }

    if (storeGetters.isPayrollEntryLocked(timeEntryId)) {
      return false;
    }

    commit('SET_PAYROLL_ENTRY_LOCK', { timeEntryId, operation });

    return Boolean(storeState.entryLocks[timeEntryId] && storeState.entryLocks[timeEntryId][operation]);
  },

  releasePayrollEntryLock({ commit }, payload = {}) {
    commit('CLEAR_PAYROLL_ENTRY_LOCK', payload);
  },

  clearAllPayrollEntryLocks({ commit }) {
    commit('CLEAR_ALL_PAYROLL_ENTRY_LOCKS');
  },
};

export default {
  state,
  getters,
  actions,
  mutations,
};
